/**
 * Карта маршрутов SPA. Публичные экраны входа/настройки/регистрации/сброса и приватный кабинет
 * (под RequireAuth) с командными разделами под ролевыми стражами. Разделы поздних блоков
 * показываются как заглушки, чтобы навигация каркаса работала целиком.
 */
import { Route, Routes } from 'react-router-dom'
import { PublicOnly, RequireAuth, RequireRole, RequireTeam, SetupRoute } from './guards'
import { CabinetLayout } from './cabinet-layout'
import { LoginPage } from '@/pages/auth/login'
import { SetupPage } from '@/pages/auth/setup'
import { RegisterPage } from '@/pages/auth/register'
import { ResetPage } from '@/pages/auth/reset'
import { SelectOrganizationPage } from '@/pages/auth/select-organization'
import { HomePage } from '@/pages/home'
import { SettingsPage } from '@/pages/settings'
import { HelpPage } from '@/pages/help'
import { NotFoundPage } from '@/pages/not-found'
import { StaffPage } from '@/pages/staff'
import { EmployeePage } from '@/pages/employee'
import { OrdersPage } from '@/pages/orders'
import { QuestionsPage } from '@/pages/questions'
import { QuestionPage } from '@/pages/question'
import { OrderDetailPage } from '@/pages/order-detail'
import { OrderSoutWizardPage } from '@/pages/order-sout-wizard'
import { OrderSoutWorkplacePage } from '@/pages/order-sout-workplace'
import { OrderSoutWorkplaceNewPage } from '@/pages/order-sout-workplace-new'
import { OrderProfriskWizardPage } from '@/pages/order-profrisk-wizard'
import { OrderEcologyWizardPage } from '@/pages/order-ecology-wizard'
import { OrderTrainingRequestPage } from '@/pages/order-training-request'
import { SoutDashboardPage } from '@/pages/sout-dashboard'
import { ProfriskDashboardPage } from '@/pages/profrisk-dashboard'
import { OtOverviewPage } from '@/pages/ot-overview'
import { EcologyOverviewPage } from '@/pages/ecology-overview'
import { EcologyLabPage, EcologyProjectsPage, EcologyReportsPage } from '@/pages/ecology-items'
import { EcologySubscriptionPage } from '@/pages/ecology-subscription'
import { EcologySurveyPage } from '@/pages/ecology-survey'
import { WorkplacePage } from '@/pages/workplace'
import { TrainingJournalPage } from '@/pages/training-journal'
import { TrainingProgramsPage } from '@/pages/training-programs'
import { PositionsPage } from '@/pages/positions'
import { TeamTrainingPage } from '@/pages/team/training'
import { DocumentsPage } from '@/pages/documents'
import { ClientsPage } from '@/pages/team/clients'
import { ClientDetailPage } from '@/pages/team/client-detail'
import { AccountsPage } from '@/pages/team/accounts'
import { OrdersQueuePage } from '@/pages/team/orders'
import { TeamQuestionsPage } from '@/pages/team/questions'
import { TeamQuestionPage } from '@/pages/team/question'
import { TeamOrderDetailPage } from '@/pages/team/order-detail'
import { TeamDocumentsPage } from '@/pages/team/documents'
import { AuditPage } from '@/pages/team/audit'
import { AdminNotificationsPage } from '@/pages/team/admin-notifications'
import { TeamEcologyPage } from '@/pages/team/ecology'
import { ClientEcologySurveyPage } from '@/pages/team/client-ecology-survey'

export function AppRoutes() {
  return (
    <Routes>
      {/* Публичные */}
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
      {/* Сброс пароля: `/reset` — запрос письма, `/reset?token=…` — новый пароль по ссылке из
          письма (зеркало бэкенда — `domain.PasswordResetPath`). `/reset-password` — запасной
          адрес: на него вели письма до 06.10.2026, и страницы по нему не было. */}
      <Route path="/reset" element={<PublicOnly><ResetPage /></PublicOnly>} />
      <Route path="/reset-password" element={<PublicOnly><ResetPage /></PublicOnly>} />
      <Route path="/setup" element={<SetupRoute><SetupPage /></SetupRoute>} />
      {/* Окно выбора организации (multi-organization-login): клиент-компания с несколькими
          организациями выбирает, в какую зайти; без организации остального кабинета нет. */}
      <Route
        path="/organizations"
        element={
          <RequireAuth organizationOptional>
            <SelectOrganizationPage />
          </RequireAuth>
        }
      />

      {/* Приватный кабинет */}
      <Route element={<RequireAuth><CabinetLayout /></RequireAuth>}>
        <Route path="/" element={<HomePage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* Инструкция для клиента (help-page) — только компания, контент про сценарии СОУТ/
            структуры/профрисков клиенту-физлицу не подходит. */}
        <Route
          path="/help"
          element={
            <RequireRole roles={['company']}>
              <HelpPage />
            </RequireRole>
          }
        />

        {/* Обучение (Блок 5): каталог программ — команда (training-packages-approval: правит
            админ, остальные добавляют на подтверждение); журнал сроков — клиент (своя орг.) */}
        <Route
          path="/training/programs"
          element={
            <RequireRole roles={['admin', 'head', 'employee']}>
              <TrainingProgramsPage />
            </RequireRole>
          }
        />
        {/* Справочник должностей (p2-position-dictionary): глобальный, правит только админ —
            рядом с каталогом программ обучения, тот же паттерн. */}
        <Route
          path="/positions"
          element={
            <RequireRole roles={['admin']}>
              <PositionsPage />
            </RequireRole>
          }
        />
        <Route
          path="/training"
          element={
            <RequireRole roles={['company', 'individual']}>
              <TrainingJournalPage />
            </RequireRole>
          }
        />
        <Route
          path="/sout"
          element={
            <RequireRole roles={['company']}>
              <SoutDashboardPage />
            </RequireRole>
          }
        />
        <Route
          path="/profrisks"
          element={
            <RequireRole roles={['company']}>
              <ProfriskDashboardPage />
            </RequireRole>
          }
        />
        {/* Охрана труда (Блок p2-ot-section, §9.2/§9.5): главная страница раздела + карточка
            рабочего места. Обучение/Спецоценка/Профриски выше — те же маршруты, без изменений
            (теперь доступны только как подразделы «ot» в сайдбаре, см. app/nav.ts). */}
        <Route
          path="/ot"
          element={
            <RequireRole roles={['company']}>
              <OtOverviewPage />
            </RequireRole>
          }
        />
        {/* Экология (ecology-sites, вариант Б): обзор с площадками и подразделы «Проектные работы» /
            «Отчётность» (подпункты «ecology» в сайдбаре, см. app/nav.ts). */}
        <Route
          path="/ecology"
          element={
            <RequireRole roles={['company']}>
              <EcologyOverviewPage />
            </RequireRole>
          }
        />
        <Route
          path="/ecology/projects"
          element={
            <RequireRole roles={['company']}>
              <EcologyProjectsPage />
            </RequireRole>
          }
        />
        <Route
          path="/ecology/reports"
          element={
            <RequireRole roles={['company']}>
              <EcologyReportsPage />
            </RequireRole>
          }
        />
        {/* Лабораторный контроль (ecology-lab-control): замеры площадок по ближайшему сроку. */}
        <Route
          path="/ecology/lab"
          element={
            <RequireRole roles={['company']}>
              <EcologyLabPage />
            </RequireRole>
          }
        />
        {/* Абонентское обслуживание (ecology-subscription): сопровождение или предложение тарифов. */}
        <Route
          path="/ecology/subscription"
          element={
            <RequireRole roles={['company']}>
              <EcologySubscriptionPage />
            </RequireRole>
          }
        />
        <Route
          path="/ecology/survey"
          element={
            <RequireRole roles={['company']}>
              <EcologySurveyPage />
            </RequireRole>
          }
        />
        <Route
          path="/workplaces/:id"
          element={
            <RequireRole roles={['company']}>
              <WorkplacePage />
            </RequireRole>
          }
        />
        <Route
          path="/orders"
          element={
            <RequireRole roles={['company', 'individual']}>
              <OrdersPage />
            </RequireRole>
          }
        />
        <Route
          path="/orders/:id/sout"
          element={
            <RequireRole roles={['company']}>
              <OrderSoutWizardPage />
            </RequireRole>
          }
        />
        {/* Карточка рабочего места и ручное добавление — отдельные страницы (p3-sout-workplaces
            §4/§14), не форма внутри общего списка. `new` — статичный сегмент, не конфликтует
            с `:lineId` (react-router ранжирует точные сегменты выше динамических). */}
        <Route
          path="/orders/:id/sout/workplaces/new"
          element={
            <RequireRole roles={['company']}>
              <OrderSoutWorkplaceNewPage />
            </RequireRole>
          }
        />
        <Route
          path="/orders/:id/sout/workplaces/:lineId"
          element={
            <RequireRole roles={['company']}>
              <OrderSoutWorkplacePage />
            </RequireRole>
          }
        />
        <Route
          path="/orders/:id/profrisk"
          element={
            <RequireRole roles={['company']}>
              <OrderProfriskWizardPage />
            </RequireRole>
          }
        />
        {/* Мастер заказа услуги по экологии (ecology-order). */}
        <Route
          path="/orders/:id/ecology"
          element={
            <RequireRole roles={['company']}>
              <OrderEcologyWizardPage />
            </RequireRole>
          }
        />
        <Route
          path="/orders/:id/training"
          element={
            <RequireRole roles={['company', 'individual']}>
              <OrderTrainingRequestPage />
            </RequireRole>
          }
        />
        <Route
          path="/orders/:id"
          element={
            <RequireRole roles={['company', 'individual']}>
              <OrderDetailPage />
            </RequireRole>
          }
        />
        {/* «Мои вопросы» (client-questions; ТЗ п.9.7) — компания и физлицо. */}
        <Route
          path="/questions"
          element={
            <RequireRole roles={['company', 'individual']}>
              <QuestionsPage />
            </RequireRole>
          }
        />
        <Route
          path="/questions/:id"
          element={
            <RequireRole roles={['company', 'individual']}>
              <QuestionPage />
            </RequireRole>
          }
        />
        <Route
          path="/staff"
          element={
            <RequireRole roles={['company']}>
              <StaffPage />
            </RequireRole>
          }
        />
        <Route
          path="/staff/:id"
          element={
            <RequireRole roles={['company']}>
              <EmployeePage />
            </RequireRole>
          }
        />
        <Route
          path="/documents"
          element={
            <RequireRole roles={['company', 'individual']}>
              <DocumentsPage />
            </RequireRole>
          }
        />

        {/* Командные разделы. Доступные всей команде (admin+employee) — под RequireTeam;
            строго админские (аккаунты, админ-уведомления) — под RequireRole(['admin']). */}
        <Route
          path="/team/clients"
          element={
            <RequireTeam>
              <ClientsPage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/clients/:cid"
          element={
            <RequireTeam>
              <ClientDetailPage />
            </RequireTeam>
          }
        />
        {/* Опрос по экологии клиента (ecology-survey): мастер по площадке, «Назад» — вкладка «Экология». */}
        <Route
          path="/team/clients/:cid/ecology/survey"
          element={
            <RequireTeam>
              <ClientEcologySurveyPage />
            </RequireTeam>
          }
        />
        {/* Карточка рабочего места выбранного клиента для команды (centrika-docs!61): cid в пути
            переживает прямую ссылку/перезагрузку, «Назад» ведёт в карточку клиента, вкладка «Структура». */}
        <Route
          path="/team/clients/:cid/workplaces/:id"
          element={
            <RequireTeam>
              <WorkplacePage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/orders"
          element={
            <RequireTeam>
              <OrdersQueuePage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/questions"
          element={
            <RequireTeam>
              <TeamQuestionsPage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/questions/:id"
          element={
            <RequireTeam>
              <TeamQuestionPage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/orders/:id"
          element={
            <RequireTeam>
              <TeamOrderDetailPage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/accounts"
          element={
            <RequireRole roles={['admin']}>
              <AccountsPage />
            </RequireRole>
          }
        />
        <Route
          path="/team/training"
          element={
            <RequireTeam>
              <TeamTrainingPage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/documents"
          element={
            <RequireTeam>
              <TeamDocumentsPage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/audit"
          element={
            <RequireTeam>
              <AuditPage />
            </RequireTeam>
          }
        />
        {/* Раздел «Экология» панели команды: справочник, матрица «Кому что нужно», опрос и сроки —
            только админ (ecology-catalog); абонентов ведёт и сотрудник (ecology-subscription),
            поэтому страница доступна всей команде, а состав вкладок выбирается по роли. */}
        <Route
          path="/team/ecology"
          element={
            <RequireTeam>
              <TeamEcologyPage />
            </RequireTeam>
          }
        />
        <Route
          path="/team/notifications"
          element={
            <RequireRole roles={['admin']}>
              <AdminNotificationsPage />
            </RequireRole>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
